import React, { useState } from 'react';

const HowWeCalculate = ({ vm }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const styles = {
    container: {
      marginBottom: '32px',
      padding: '20px',
      background: '#f8fafc',
      borderRadius: '12px',
      border: '1px solid #e2e8f0'
    },
    summary: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      padding: '12px 16px',
      borderRadius: '8px',
      background: '#f7f9fc'
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: '#1e293b'
    },
    chevron: {
      transition: 'transform 0.2s',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
    },
    content: {
      marginTop: '16px',
      paddingTop: '16px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1.2fr 0.8fr',
      gap: '24px'
    },
    leftColumn: {
      color: '#475569'
    },
    panel: {
      background: '#f7f4ff',
      border: '1px solid #e0def7',
      borderRadius: '12px',
      padding: '16px'
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px',
      marginTop: '16px'
    },
    firstSectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '8px',
      marginTop: '0'
    },
    list: {
      margin: '8px 0 16px 0',
      paddingLeft: '20px',
      color: '#64748b',
      lineHeight: '1.6'
    },
    listItem: {
      marginBottom: '4px'
    },
    paragraph: {
      margin: '8px 0 16px 0',
      color: '#64748b',
      lineHeight: '1.6'
    },
    code: {
      background: '#f1f5f9',
      padding: '2px 6px',
      borderRadius: '4px',
      fontFamily: 'monospace',
      fontSize: '0.9em'
    },
    panelTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px'
    },
    panelList: {
      margin: 0,
      paddingLeft: '20px',
      color: '#4c1d95'
    },
    panelItem: {
      marginBottom: '6px',
      lineHeight: '1.5'
    },
    strong: {
      fontWeight: '600',
      color: '#1e293b'
    },
    mobileGrid: {
      display: 'block'
    }
  };

  const i = vm?.indices || {};

  // Responsividade simples
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 900;

  return (
    <div style={styles.container}>
      <div 
        style={styles.summary}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={styles.title}>Como calculamos os índices</span>
        <svg 
          style={styles.chevron}
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      
      {isOpen && (
        <div style={styles.content}>
          <div style={isMobile ? styles.mobileGrid : styles.grid}>
            <div style={styles.leftColumn}>
              <h4 style={styles.firstSectionTitle}>Estrutura do questionário</h4>
              <ul style={styles.list}>
                <li style={styles.listItem}>20 itens em escala 1–6.</li>
                <li style={styles.listItem}>Q1–Q15 = Exposição (Risco). Q16–Q20 = Maturidade.</li>
              </ul>

              <h4 style={styles.sectionTitle}>O que conta como "positivo"</h4>
              <p style={styles.paragraph}>Somente respostas <strong>5</strong> ou <strong>6</strong>.</p>

              <h4 style={styles.sectionTitle}>Cálculo dos índices (0–6)</h4>
              <ul style={styles.list}>
                <li style={styles.listItem}><span style={styles.code}>IE = (positivos_risco ÷ 15) × 6</span></li>
                <li style={styles.listItem}><span style={styles.code}>IM = (positivos_maturidade ÷ 5) × 6</span></li>
              </ul>

              <h4 style={styles.sectionTitle}>Gráficos</h4>
              <ul style={styles.list}>
                <li style={styles.listItem}>Termômetro (0–5): <span style={styles.code}>(IE ÷ 6) × 5</span> (visual)</li>
                <li style={styles.listItem}>Radar por eixo (0–6): <span style={styles.code}>(resposta − 1) × (6 ÷ 5)</span></li>
              </ul>

              <h4 style={styles.sectionTitle}>Classificações</h4>
              <p style={styles.paragraph}>
                <strong>Exposição</strong> (por % de positivos em Q1–Q15): Vermelho ≤33,3% | Laranja ≤50,0% | Amarelo ≤66,7% | Verde &gt;66,7%.
              </p>
              <p style={styles.paragraph}>
                <strong>Maturidade</strong> (IM): Reativo ≤2,0 | Defensivo 2,1–3,0 | Em Progresso 3,1–4,4 | Avançado 4,5–5,4 | Estratégico 5,5–6,0.
              </p>

              <h4 style={styles.sectionTitle}>Regras de exibição</h4>
              <ul style={styles.list}>
                <li style={styles.listItem}>Cálculo interno com 4 casas; exibição com 1 casa (pt-BR).</li>
                <li style={styles.listItem}>Empate nos cortes pertence à faixa inferior.</li>
              </ul>
            </div>

            {vm && (
              <div style={styles.panel}>
                <h4 style={styles.panelTitle}>Seus números</h4>
                <ul style={styles.panelList}>
                  <li style={styles.panelItem}>Positivos em Exposição (Q1–Q15): <span style={styles.strong}>{i.posRisco}</span> / 15</li>
                  <li style={styles.panelItem}>Positivos em Maturidade (Q16–Q20): <span style={styles.strong}>{i.posMat}</span> / 5</li>
                  <li style={styles.panelItem}>IE (0–6): <span style={styles.strong}>{i.IE_fmt}</span> · Termômetro (0–5): <span style={styles.strong}>{i.IE_termo_fmt}</span></li>
                  <li style={styles.panelItem}>IM (0–6): <span style={styles.strong}>{i.IM_fmt}</span></li>
                  <li style={styles.panelItem}>Faixa de Exposição: <span style={styles.strong}>{i.faixa}</span> · Nível de Maturidade: <span style={styles.strong}>{i.nivelIM}</span></li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HowWeCalculate;